import { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function AdminCleanupTestUsers() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const scan = async () => {
    setBusy(true);
    setPreview(null);
    try {
      const { data } = await api.post("/admin/cleanup-test-users", { dry_run: true });
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't scan for test users.");
    } finally { setBusy(false); }
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) scan();
  };

  const purge = async () => {
    if (!preview?.matched_users) return;
    if (!window.confirm(`Permanently delete ${preview.matched_users} test users and all their data? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup-test-users", { dry_run: false });
      toast.success(`Deleted ${data.deleted_users} users, ${data.deleted_predictions} predictions.`);
      setOpen(false);
      setPreview(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cleanup failed.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button data-testid="admin-cleanup-btn"
          title="Remove seeded/test accounts to protect email deliverability"
          className="hidden sm:inline-flex items-center gap-1.5 px-2 py-1 border border-border text-[10px] uppercase tracking-wider text-muted-foreground hover:border-destructive/60 hover:text-foreground transition-colors">
          <Sparkles size={12} /> Purge Tests
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card border-2 border-foreground rounded-none max-w-md" data-testid="admin-cleanup-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase flex items-center gap-2">
            <Sparkles className="text-destructive" size={24} /> Purge Test Users
          </DialogTitle>
          <DialogDescription className="font-data text-xs">
            Removes seeded accounts on non-deliverable domains (e.g. @predictor.com, @x.com) and all their
            predictions, referrals and push subscriptions. Protects your email sending reputation. Admins are never deleted.
          </DialogDescription>
        </DialogHeader>

        {busy && !preview && (
          <p className="font-data text-xs text-muted-foreground" data-testid="admin-cleanup-scanning">Scanning…</p>
        )}

        {preview && (
          <div className="space-y-3">
            <div className="border-2 border-foreground p-3">
              <p className="font-display text-4xl leading-none" data-testid="admin-cleanup-count">{preview.matched_users}</p>
              <p className="font-data text-[10px] uppercase tracking-wider text-muted-foreground">test users found</p>
            </div>
            {preview.sample?.length > 0 && (
              <ul className="font-data text-[11px] text-muted-foreground space-y-0.5 max-h-32 overflow-auto" data-testid="admin-cleanup-sample">
                {preview.sample.map((e) => <li key={e} className="truncate">• {e}</li>)}
                {preview.matched_users > preview.sample.length && (
                  <li className="opacity-60">…and {preview.matched_users - preview.sample.length} more</li>
                )}
              </ul>
            )}
            <Button type="button" disabled={busy || !preview.matched_users} onClick={purge}
              data-testid="admin-cleanup-purge"
              className="w-full rounded-none bg-destructive text-destructive-foreground hover:brightness-110 uppercase text-xs font-bold">
              <Trash2 size={14} className="me-1" />
              {busy ? "Purging…" : preview.matched_users ? `Delete ${preview.matched_users} test users` : "Nothing to clean"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
